import React, { useMemo, useContext } from 'react'
import { useNewTranslation } from 'hooks'
import { STEP_TYPE_ENUM } from 'services/api/constants'
import { useFormikContext } from 'formik'
import { EditorValues } from './types'
import { generateUuid } from 'services/uuid'
import { useSelector } from 'react-redux'
import { selectIsEngagementStepTypeHelper, selectTaskPlatformContentTypesByName } from 'state/constants'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from 'components/Campaign'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import PopoverButton, { PopoverItemProps } from 'components/Shared/Button/PopoverButton'

enum SelectedStepMenuType {
    CUSTOM = 1,
    ENGAGEMENT,
}

const AddStepButton: React.FC<{
    campaignStatus?: CAMPAIGN_STATUS_ENUM
    onStepAdded: VoidFunction
}> = ({ campaignStatus, onStepAdded }) => {
    const [t] = useNewTranslation()
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)

    const taskPlatformContentTypesByName = useSelector(selectTaskPlatformContentTypesByName)
    const viewType = useContext(ViewTypeContext)

    const { values: formikValues, setValues } = useFormikContext<EditorValues>()

    const alreadyHaveEngagementStepType = useMemo(
        () => formikValues.steps.some(s => isEngagementStepType(s.type)),
        [formikValues, isEngagementStepType],
    )
    const taskEngagementStepType = useMemo(() => {
        return (
            formikValues.platform_content_type &&
            taskPlatformContentTypesByName[formikValues.platform_content_type].engagement_step_type
        )
    }, [formikValues.platform_content_type, taskPlatformContentTypesByName])

    const handleAddStep = (type: SelectedStepMenuType): void => {
        if (type === SelectedStepMenuType.ENGAGEMENT && !taskEngagementStepType && alreadyHaveEngagementStepType) {
            // cannot add engagement step when
            // 1. the task is already have engagement step type
            // 2. or the task engagementStepType is null
            return
        }

        const newStep =
            type === SelectedStepMenuType.CUSTOM
                ? ({
                      type: STEP_TYPE_ENUM.CUSTOM,
                      time_window: { start: null, end: null },
                      data: {
                          title: '',
                          blocks: [],
                      },
                      uuid: generateUuid(),
                  } as any)
                : {
                      type: taskEngagementStepType,
                      time_window: { start: null, end: null },
                      data: {
                          title: '',
                      },
                      uuid: generateUuid(),
                  }

        const newStepsArray = [...formikValues.steps]
        if (type === SelectedStepMenuType.ENGAGEMENT) {
            // insert newStep to the last array
            newStepsArray.splice(newStepsArray.length, 0, newStep)
        } else if (alreadyHaveEngagementStepType) {
            // insert newStep to the previous step of engagement step
            newStepsArray.splice(newStepsArray.length - 1, 0, newStep)
        } else {
            // insert newStep to the last array
            newStepsArray.splice(newStepsArray.length, 0, newStep)
        }

        setValues(currentValues => {
            return {
                ...currentValues,
                steps: newStepsArray,
            }
        })
        onStepAdded()
        setTimeout(() => {
            const stepEditorElement = document.getElementById(newStep.uuid)
            if (stepEditorElement) {
                stepEditorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
        }, 0)
    }

    const customStepMenu: PopoverItemProps[] = [
        {
            itemPrimaryLabel: t('campaignEditor.createTask.addInstructionStepHeader'),
            itemSecondaryLabel: t('campaignEditor.createTask.addInstructionStepDescription'),
            onClick(e: any) {
                handleAddStep(SelectedStepMenuType.CUSTOM)
            },
            show: true,
            itemPrimaryLabelProps: { scale: 'bodyS700', color: 'secondary' },
            itemSecondaryLabelProps: { scale: 'bodyS400', color: 'secondary' },
            icon: 'listNumbers',
        },
        {
            itemPrimaryLabel: t('campaignEditor.createTask.addProofEngagementStepHeader'),
            itemSecondaryLabel: t('campaignEditor.createTask.addProofEngagementStepDescription'),
            onClick(e: any) {
                handleAddStep(SelectedStepMenuType.ENGAGEMENT)
            },
            show: true,
            itemPrimaryLabelProps: {
                scale: 'bodyS700',
                color: !taskEngagementStepType || alreadyHaveEngagementStepType ? 'placeholder' : 'secondary',
            },
            itemSecondaryLabelProps: {
                scale: 'bodyS400',
                color: !taskEngagementStepType || alreadyHaveEngagementStepType ? 'placeholder' : 'secondary',
            },
            icon: 'usersThree',
            disabled: !taskEngagementStepType || alreadyHaveEngagementStepType,
        },
    ]

    return (
        <>
            <PopoverButton
                popoverTitleText={t('campaignEditor.createTask.addStepMenuHeader')}
                popoverActions={customStepMenu}
                buttonProps={{
                    label: t('button.addStep'),
                    icon: 'plus',
                    isDisabled:
                        viewType !== EDITOR_ROUTE_ENUM.EDIT ||
                        !(
                            campaignStatus == CAMPAIGN_STATUS_ENUM.DRAFT ||
                            campaignStatus == CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
                            campaignStatus == CAMPAIGN_STATUS_ENUM.FOR_EDIT
                        ) ||
                        !formikValues.platform_content_type,
                }}
            />
        </>
    )
}

export default AddStepButton
